import { Dialog, DialogTitle } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";

import { MetadataFieldTypes } from "@stw/yup-validators";

import {
  MetadataBoolFieldFragment,
  MetadataEnumFieldFragment,
  MetadataEnumMultiFieldFragment,
  MetadataStringFieldFragment,
  MetadataUserListFieldFragment,
  MetadataStateFieldFragment,
  MetadataFieldsFragment,
} from "~frontend/__generated__/graphql";
import { EditBoolFieldForm } from "~frontend/components/campaignMetadata/EditBoolFieldForm";
import { EditEnumFieldForm } from "~frontend/components/campaignMetadata/EditEnumFieldForm";
import { EditEnumMultiFieldForm } from "~frontend/components/campaignMetadata/EditEnumMultiFieldForm";
import { EditStateFieldForm } from "~frontend/components/campaignMetadata/EditStateFieldForm";
import { EditStringFieldForm } from "~frontend/components/campaignMetadata/EditStringFieldForm";
import { EditUserListFieldForm } from "~frontend/components/campaignMetadata/EditUserListFieldForm";
import { mapValueTypenameToFieldType } from "~frontend/components/campaignMetadata/common";

export function EditFieldDialog({
  open,
  closeDialog,
  editField,
}: {
  open: boolean;
  closeDialog: () => void;
  editField?: MetadataFieldsFragment;
}): ReactElement {
  const initialFieldType = editField
    ? mapValueTypenameToFieldType(editField.__typename)
    : MetadataFieldTypes.STRING;
  const [fieldType, setFieldType] =
    useState<MetadataFieldTypes>(initialFieldType);
  const [fieldName, setFieldName] = useState("");

  useEffect(() => {
    // if the dialog is closed, reset the field name to the original value
    if (!open) {
      setFieldName("");
    } else {
      setFieldName(editField?.name ?? "");
    }
  }, [open, editField]);

  function onFieldTypeChange(name: string, fieldType: MetadataFieldTypes) {
    // if the field type is changed, hold onto the value input into the name field.
    setFieldName(name);
    setFieldType(fieldType);
  }

  function renderMetadataDialog() {
    switch (fieldType) {
      case MetadataFieldTypes.STRING:
        return (
          <EditStringFieldForm
            onTypeChange={onFieldTypeChange}
            editField={editField as MetadataStringFieldFragment}
            initialName={fieldName}
            onCancelClick={closeDialog}
          />
        );
      case MetadataFieldTypes.BOOLEAN:
        return (
          <EditBoolFieldForm
            onTypeChange={onFieldTypeChange}
            initialName={fieldName}
            editField={editField as MetadataBoolFieldFragment}
            onCancelClick={closeDialog}
          />
        );
      case MetadataFieldTypes.ENUM:
        return (
          <EditEnumFieldForm
            onTypeChange={onFieldTypeChange}
            initialName={fieldName}
            editField={editField as MetadataEnumFieldFragment}
            onCancelClick={closeDialog}
          />
        );
      case MetadataFieldTypes.ENUM_MULTI:
        return (
          <EditEnumMultiFieldForm
            onTypeChange={onFieldTypeChange}
            initialName={fieldName}
            editField={editField as MetadataEnumMultiFieldFragment}
            onCancelClick={closeDialog}
          />
        );
      case MetadataFieldTypes.USER_LIST:
        return (
          <EditUserListFieldForm
            onTypeChange={onFieldTypeChange}
            initialName={fieldName}
            editField={editField as MetadataUserListFieldFragment}
            onCancelClick={closeDialog}
          />
        );
      case MetadataFieldTypes.STATE:
      case MetadataFieldTypes.STATE_MULTI:
        return (
          <EditStateFieldForm
            onTypeChange={onFieldTypeChange}
            initialName={fieldName}
            editField={editField as MetadataStateFieldFragment}
            onCancelClick={closeDialog}
          />
        );
      default:
        return <></>;
    }
  }
  return (
    <Dialog open={open} maxWidth={"sm"} fullWidth>
      <DialogTitle>{editField ? "Update" : "New"} Metadata Field</DialogTitle>
      {renderMetadataDialog()}
    </Dialog>
  );
}
